import React, { ChangeEventHandler, MutableRefObject, ReactNode, forwardRef } from 'react';
import cn from 'classnames';
import styles from './SettingsComponents.module.scss';
import { Overlay, Tooltip } from '@repo/components/tooltips';
import { XIcon, ChevronLeftIcon } from '@repo/icons';
import { useTheme } from '@repo/themes';
import { SmallButton } from '@components/Button';

export const Section = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn(styles.section, className)}>{children}</div>
);

export const SettingsSectionLabel = ({
  children,
  error,
  tooltip,
}: {
  children: React.ReactNode;
  error?: string;
  tooltip?: string;
}) => (
  <div className={styles.sectionLabel}>
    <label className="form-label">
      {children}
      {tooltip && (
        <Tooltip
          placement="top"
          overlay={<Overlay className={styles.tooltipOverlay}>{tooltip}</Overlay>}
        >
          <div className={styles.moreInfo}>?</div>
        </Tooltip>
      )}
    </label>
    {error && <div className={styles.error}>{error}</div>}
  </div>
);

export const SettingsSectionDescription = ({ children }) => (
  <div className={styles.sectionDescription}>{children}</div>
);

export const SettingsPanelHeader = ({
  title,
  closeSettingsDisplay,
  onBack,
}: {
  title: string;
  closeSettingsDisplay: () => void;
  onBack?: () => void;
}) => (
  <div className={styles.panelHeader}>
    {onBack && (
      <button className={styles.backBtn} onClick={onBack}>
        <ChevronLeftIcon width={6.5} strokeWidth={2.2} />
      </button>
    )}
    <h2 className={styles.title}>{title}</h2>
    <button className={styles.closeBtn} onClick={closeSettingsDisplay}>
      <XIcon width={12} height={12} />
    </button>
  </div>
);

export const SettingsBoxesWrapper = ({ children }) => (
  <div className={styles.boxesWrapper}>{children}</div>
);

export const SettingsSectionBox = ({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) => (
  <div className={cn(styles.sectionBox, className)} style={style}>
    {children}
  </div>
);

export const SettingsSectionRow = forwardRef(
  (
    {
      children,
      className,
      style,
    }: {
      children: ReactNode;
      className?: string;
      style?: React.CSSProperties;
    },
    ref: MutableRefObject<HTMLDivElement>,
  ) => (
    <div className={cn(styles.sectionRow, className)} ref={ref} style={style}>
      {children}
    </div>
  ),
);

export const SettingsButton = ({
  children,
  onClick,
  className,
  isInsideBox,
  alwaysRender = false,
  disabled,
  asLink,
  href,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  isInsideBox?: boolean;
  alwaysRender?: boolean;
  disabled?: boolean;
  asLink?: boolean;
  href?: string;
}) => {
  const [theme] = useTheme();

  if (asLink) {
    return (
      <a
        className={cn(
          styles.button,
          styles[theme],
          isInsideBox && styles.inBox,
          alwaysRender && styles.noAnimation,
          className,
        )}
        href={href}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      className={cn(
        styles.button,
        styles[theme],
        isInsideBox && styles.inBox,
        alwaysRender && styles.noAnimation,
        className,
      )}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export const SettingsInlineButton = ({
  children,
  onClick,
  className,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
}) => (
  <SmallButton className={cn(styles.inlineButton, className)} onClick={onClick} disabled={disabled}>
    {children}
  </SmallButton>
);

export function SettingToggle({
  title,
  checked,
  name,
  onChange,
  disabled,
  tooltip,
  beforeToggle,
}: {
  title: string;
  checked: boolean;
  name: string;
  onChange: ChangeEventHandler;
  disabled?: boolean;
  tooltip?: string;
  beforeToggle?: ReactNode;
}) {
  return (
    <div className={styles.toggle}>
      <span className={styles.toggleTitle}>
        {title}
        {tooltip && (
          <Tooltip
            placement="top"
            overlay={<Overlay className={styles.tooltipOverlay}>{tooltip}</Overlay>}
          >
            <div className={styles.moreInfo}>?</div>
          </Tooltip>
        )}
      </span>
      <span className={styles.toggleInputContainer}>
        {beforeToggle}
        <label className="form-toggle">
          <input
            name={name}
            className="form-toggle-input"
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
          />
          <span className="form-toggle-switch" />
        </label>
      </span>
    </div>
  );
}
